import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight, Download, Copy, Printer, Columns } from 'lucide-react'
import toast from 'react-hot-toast'
import { showDeleteConfirm, showSuccess, showError } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { empSkillService } from '@/services/empSkillService'
import { contractService } from '@/services/contractService'
import type { SkillListItem } from '@/api/types/empSkill.types'
import type { EmployeeOption } from '@/api/types'

function formatDate(d?: string): string {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
  } catch {
    return String(d).slice(0, 10)
  }
}

export const EmpSkillList: React.FC = () => {
  const { t } = useTranslation('common')
  const ts = (key: string) => t(`empSkill.${key}`)
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [list, setList] = useState<SkillListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  const [skillTypes, setSkillTypes] = useState<{ id?: number; name?: string; lookupNameEn?: string; lookupNameAr?: string }[]>([])
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    employeeName: true,
    skillType: true,
    scoreOutOf100: true,
    experienceYear: true,
    createdDate: true,
    action: true,
  })

  useEffect(() => {
    if (!branchId) return
    contractService.getAllEmployees(branchId).then((data) => setEmployees(data ?? [])).catch(() => setEmployees([]))
  }, [branchId])

  useEffect(() => {
    empSkillService.getSkillTypes().then((data) => setSkillTypes(data ?? [])).catch(() => setSkillTypes([]))
  }, [])

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const res = await empSkillService.getAll(branchId, 1, 1000)
        setList(res.data ?? [])
        setTotalRecords(res.totalRecords ?? 0)
      } catch (e: any) {
        const status = e?.response?.status
        const msg = (e?.response?.data?.message ?? e?.message ?? '').toLowerCase()
        const isNoData = status === 404 || /no data|not found|no records/.test(msg)
        setList([])
        setTotalRecords(0)
        if (!isNoData) toast.error(e?.response?.data?.message ?? ts('fetchFailed'))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId])

  const getEmployeeName = (empId?: number): string => {
    if (!empId) return '—'
    const emp = employees.find((e) => e.empId === empId)
    if (!emp) return t('empSkill.employeeFallback', { id: empId })
    return (
      (emp.fullName ?? [emp.empFirstNameEn, emp.empSecondNameEn, emp.empLastNameEn].filter(Boolean).join(' ').trim()) ||
      t('empSkill.employeeFallback', { id: empId })
    )
  }

  const getSkillTypeName = (row: SkillListItem): string => {
    if (row.skillTypeName) return row.skillTypeName
    const skillTypeId = row.skillTypeId ?? row.empSkillTypeId
    if (skillTypeId) {
      const st = skillTypes.find((s) => (s.id ?? 0) === skillTypeId)
      if (st) {
        return (
          (st as any).lookupNameEn ??
          (st as any).lookupNameAr ??
          (st as any).name ??
          t('empSkill.typeFallback', { id: skillTypeId })
        )
      }
      return t('empSkill.typeFallback', { id: skillTypeId })
    }
    return '—'
  }

  const filteredList = useMemo(() => {
    if (!searchQuery.trim()) return list
    const q = searchQuery.toLowerCase()
    return list.filter(
      (r) =>
        getEmployeeName(r.empId).toLowerCase().includes(q) ||
        getSkillTypeName(r).toLowerCase().includes(q) ||
        String((r.scoreOutOf100 ?? r.empScoreOutOf100) ?? '').toLowerCase().includes(q) ||
        String((r.experienceYear ?? r.empExperienceYear) ?? '').toLowerCase().includes(q)
    )
  }, [list, searchQuery, employees, skillTypes, t])

  const paginatedRows = useMemo(
    () => filteredList.slice((pageNumber - 1) * pageSize, pageNumber * pageSize),
    [filteredList, pageNumber, pageSize]
  )
  const filteredTotal = filteredList.length
  const totalPages = Math.max(1, Math.ceil(filteredTotal / pageSize))
  const visibleColumnCount =
    [columnVisibility.employeeName, columnVisibility.skillType, columnVisibility.scoreOutOf100, columnVisibility.experienceYear, columnVisibility.createdDate, columnVisibility.action].filter(Boolean).length || 1

  const handleAdd = () => navigate('/skills/new')
  const handleEdit = (id: number) => navigate(`/skills/${id}/edit`)

  const handleDelete = async (id: number) => {
    const confirmed = await showDeleteConfirm(ts('deleteTitle'), ts('deleteConfirm'))
    if (!confirmed) return
    setDeletingId(id)
    try {
      await empSkillService.delete(id)
      await showSuccess(ts('deletedTitle'), ts('deletedMessage'))
      if (branchId) {
        const res = await empSkillService.getAll(branchId, 1, 1000)
        setList(res.data ?? [])
        setTotalRecords(res.totalRecords ?? 0)
      }
    } catch (e: any) {
      await showError(ts('deleteFailedTitle'), e?.response?.data?.message ?? e?.message ?? ts('deleteFailedMessage'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleCsv = () => {
    const headers = [
      ts('columnEmployeeName'),
      ts('columnSkillType'),
      ts('columnScoreOutOf100'),
      ts('columnExperienceYear'),
      ts('columnCreatedDate'),
    ]
    const rows = filteredList.map((r) =>
      [
        getEmployeeName(r.empId),
        getSkillTypeName(r),
        (r.scoreOutOf100 ?? r.empScoreOutOf100) ?? '',
        (r.experienceYear ?? r.empExperienceYear) ?? '',
        r.createdDate ? formatDate(r.createdDate) : '',
      ].join(',')
    )
    const csv = [headers.join(','), ...rows].join('\r\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = ts('csvFileName')
    a.click()
    URL.revokeObjectURL(url)
    toast.success(ts('csvDownloaded'))
  }

  const handleCopy = () => {
    const text = filteredList
      .map((r) =>
        [
          getEmployeeName(r.empId),
          getSkillTypeName(r),
          (r.scoreOutOf100 ?? r.empScoreOutOf100) ?? '',
          (r.experienceYear ?? r.empExperienceYear) ?? '',
          r.createdDate ? formatDate(r.createdDate) : '',
        ].join('\t')
      )
      .join('\n')
    navigator.clipboard.writeText(text).then(() => toast.success(ts('copiedToClipboard')))
  }

  const handlePrint = () => window.print()

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={ts('listTitle')} description={ts('listDescriptionShort')} />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">{ts('selectBranchFirst')}</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={ts('listTitle')}
        description={ts('listDescription')}
        action={{ label: ts('actionAdd'), onClick: handleAdd, icon: Plus }}
      />
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('common.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Columns className="w-4 h-4 mr-2" />
                  {ts('columnVisibility')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[180px]">
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.employeeName}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, employeeName: !!v }))}
                >
                  {ts('columnEmployeeName')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.skillType}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, skillType: !!v }))}
                >
                  {ts('columnSkillType')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.scoreOutOf100}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, scoreOutOf100: !!v }))}
                >
                  {ts('columnScoreOutOf100')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.experienceYear}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, experienceYear: !!v }))}
                >
                  {ts('columnExperienceYear')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.createdDate}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, createdDate: !!v }))}
                >
                  {ts('columnCreatedDate')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.action}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, action: !!v }))}
                >
                  {ts('columnAction')}
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" onClick={handleCsv}>
              <Download className="w-4 h-4 mr-2" />
              {ts('csv')}
            </Button>
            <Button variant="outline" size="sm" onClick={handleCopy}>
              <Copy className="w-4 h-4 mr-2" />
              {ts('copy')}
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              {ts('print')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {columnVisibility.employeeName && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {ts('columnEmployeeName')}
                    </TableHead>
                  )}
                  {columnVisibility.skillType && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {ts('columnSkillType')}
                    </TableHead>
                  )}
                  {columnVisibility.scoreOutOf100 && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {ts('columnScoreOutOf100')}
                    </TableHead>
                  )}
                  {columnVisibility.experienceYear && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {ts('columnExperienceYear')}
                    </TableHead>
                  )}
                  {columnVisibility.createdDate && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {ts('columnCreatedDate')}
                    </TableHead>
                  )}
                  {columnVisibility.action && (
                    <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {ts('columnAction')}
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className="text-center py-8 text-muted-foreground">
                      {ts('loading')}
                    </TableCell>
                  </TableRow>
                ) : paginatedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className="text-center py-8 text-muted-foreground">
                      {ts('noData')}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRows.map((row) => (
                    <TableRow key={row.id}>
                      {columnVisibility.employeeName && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {getEmployeeName(row.empId)}
                        </TableCell>
                      )}
                      {columnVisibility.skillType && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{getSkillTypeName(row)}</TableCell>
                      )}
                      {columnVisibility.scoreOutOf100 && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {(row.scoreOutOf100 ?? row.empScoreOutOf100) ?? '—'}
                        </TableCell>
                      )}
                      {columnVisibility.experienceYear && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {(row.experienceYear ?? row.empExperienceYear) ?? '—'}
                        </TableCell>
                      )}
                      {columnVisibility.createdDate && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatDate(row.createdDate)}</TableCell>
                      )}
                      {columnVisibility.action && (
                        <TableCell className="px-4 py-2 text-center align-middle">
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(row.id!)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(row.id!)}
                              disabled={deletingId === row.id}
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
            <div className="text-sm text-muted-foreground">
              {t('empSkill.paginationShowing', {
                from: filteredTotal === 0 ? 0 : (pageNumber - 1) * pageSize + 1,
                to: Math.min(pageNumber * pageSize, filteredTotal),
                total: filteredTotal,
              })}
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v))
                  setPageNumber(1)
                }}
              >
                <SelectTrigger className="w-[90px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 25, 50, 100].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber <= 1 || loading}
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
                {t('common.previous')}
              </Button>
              <span className="text-sm text-muted-foreground px-2">
                {t('empSkill.pageOf', { page: pageNumber, totalPages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber >= totalPages || loading || totalRecords === 0}
                onClick={() => setPageNumber((p) => p + 1)}
              >
                {t('common.next')}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
